import React, { useEffect } from "react"
import { Container, Row, Visible } from "react-grid-system"
import { HorizontalSpacer, Spacer } from "../../components/atoms/spacers"
import Body from "../../components/atoms/typography/Body"
import RadioButtonBar from "../../components/molecules/RadioButtonBar"
import { radioButtonBarItems } from "../../utils/design-helpers"
import { RESOLUTIONS, Resolution, getDatesOnChangedResolution } from "../../utils/chart-helpers"
import dayjs from "dayjs"
import DatePicker from "../../components/atoms/inputs/DatePicker"
import Checkbox from "../../components/atoms/inputs/Checkbox"
import { ChartSettings } from "../../components/organisms/charts/SharedDataChartView"
import H1 from "../../components/atoms/typography/H1"
import Subtitle from "../../components/atoms/typography/Subtitle"
import PrimaryButton from "../../components/atoms/buttons/PrimaryButton"

export type ReportSettingsModalContentProps = {
  initial: ChartSettings
  onChange?: (settings: ChartSettings) => void
  onClickShow?: () => void
}

const ReportSettingsModalContent: React.FC<ReportSettingsModalContentProps> = ({ initial, onChange, onClickShow }) => {
  const [{ fromTimestamp, toTimestamp, resolution }, setSettings] = React.useState(initial)
  const setFromTimestamp = (fromTimestamp: string) => setSettings((settings) => ({ ...settings, fromTimestamp }))
  const setToTimestamp = (toTimestamp: string) => setSettings((settings) => ({ ...settings, toTimestamp }))

  useEffect(() => {
    onChange?.({
      fromTimestamp,
      toTimestamp,
      resolution,
    })
  }, [fromTimestamp, toTimestamp, resolution])

  const onSelectRadio = (resolution: Resolution) => {
    const { from, to } = getDatesOnChangedResolution(
      fromTimestamp,
      resolution,
      false,
      initial.fromTimestamp,
      initial.toTimestamp,
    )

    setSettings({ fromTimestamp: from, toTimestamp: to, resolution })
  }

  const onSelectFromDate = (value: string) => {
    const fromTimestamp = dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ")

    setFromTimestamp(fromTimestamp)
  }

  const onSelectToDate = (value: string) => {
    const toTimestamp = dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ")

    setToTimestamp(toTimestamp)
  }

  const onCheckAllTime = () => {
    const fromTimestamp = dayjs(initial.fromTimestamp).format("YYYY-MM-DDTHH:mm:ssZ")
    const toTimestamp = dayjs(initial.toTimestamp).format("YYYY-MM-DDTHH:mm:ssZ")
    const resolution = RESOLUTIONS.allTime

    setSettings({
      fromTimestamp,
      toTimestamp,
      resolution,
    })
  }

  return (
    <Container>
      <Row>
        <Subtitle>Report options</Subtitle>
      </Row>
      <HorizontalSpacer spacing={1} />
      <Row>
        <Visible xs>
          <HorizontalSpacer spacing={2} />
          <Body textAlign="center">Show by: </Body>
          <HorizontalSpacer spacing={1} />
        </Visible>
        <RadioButtonBar items={radioButtonBarItems} selectedValue={resolution} onClickItem={onSelectRadio} />
      </Row>
      <HorizontalSpacer spacing={1} />
      <Row align="center">
        <DatePicker
          onSelectDate={onSelectFromDate}
          value={dayjs(fromTimestamp).format("YYYY-MM-DD")}
          disabled={resolution === RESOLUTIONS.allTime}
        />
        -
        <DatePicker
          onSelectDate={onSelectToDate}
          value={dayjs(toTimestamp).format("YYYY-MM-DD")}
          disabled={resolution === RESOLUTIONS.allTime}
        />
        <Spacer spacing={1} />
        <Checkbox checked={resolution === RESOLUTIONS.allTime} onChange={onCheckAllTime} />
        <Spacer spacing={0.5} />
        <Body>All time</Body>
      </Row>
      <HorizontalSpacer spacing={1} />
      <Row>
        <PrimaryButton title="Show preview" onClick={onClickShow} />
      </Row>
    </Container>
  )
}

export default ReportSettingsModalContent
